// <s-investment-calculator>: "Keby som investoval pred 5 rokmi?"
// Uses real daily EUR prices for gold (XAU) and silver (XAG) over the last 5 years from
// metalpriceapi.com (same source as the previous store calculator). One-off amount buys metal at the
// first price of the period; each optional monthly deposit buys at that month's average price.
// Value today = all metal bought × today's price. A line chart shows the portfolio value over time.
// Prices are cached in sessionStorage for 12 hours to keep API usage low. If prices cannot be loaded,
// the merchant-entered 5-year change (block settings) is used; with neither, no figure is shown.

const CACHE_KEY = 'gv-metal-prices-5y';
const CACHE_TTL = 12 * 60 * 60 * 1000;
const KEYS = { gold: 'EURXAU', silver: 'EURXAG' };
const MONTHS = 60;

const isoDate = (date) => date.toISOString().split('T')[0];

async function loadPrices(apiKey) {
  try {
    const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
    if (cached && Date.now() - cached.time < CACHE_TTL && cached.rates) return cached.rates;
  } catch {}

  const today = new Date();
  const start = new Date(today);
  start.setFullYear(today.getFullYear() - 5);

  // The API allows at most 365 days per request, so the 5 years are split into yearly ranges.
  const ranges = [];
  let from = new Date(start);
  while (from < today) {
    const to = new Date(from);
    to.setDate(to.getDate() + 364);
    if (to > today) to.setTime(today.getTime());
    ranges.push([isoDate(from), isoDate(to)]);
    from = new Date(to);
    from.setDate(from.getDate() + 1);
  }

  const parts = await Promise.all(
    ranges.map(async ([s, e]) => {
      const url = `https://api.metalpriceapi.com/v1/timeframe?start_date=${s}&end_date=${e}&api_key=${encodeURIComponent(apiKey)}&base=EUR&currencies=EUR,XAU,XAG`;
      try {
        const response = await fetch(url);
        const data = await response.json();
        return data.success ? data.rates : {};
      } catch {
        return {};
      }
    }),
  );

  const rates = Object.assign({}, ...parts);
  if (Object.keys(rates).length === 0) return null;
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ time: Date.now(), rates }));
  } catch {}
  return rates;
}

// Live price: the same "latest" cache the price ticker and charts share (localStorage, ~3 min),
// so the calculator's "today" value always uses the current spot price, not yesterday's close.
const LATEST_KEY = 'gv-spot-ticker-latest';
const LATEST_TTL = 170 * 1000;
const LIVE_SYMBOLS = { gold: 'XAU', silver: 'XAG' };

function livePerOunce(rates, symbol) {
  const direct = Number(rates?.[`EUR${symbol}`]);
  if (Number.isFinite(direct) && direct > 0) return direct;
  const inverse = Number(rates?.[symbol]);
  return inverse > 0 ? 1 / inverse : NaN;
}

async function loadLatest(apiKey) {
  let cached = null;
  try {
    cached = JSON.parse(localStorage.getItem(LATEST_KEY) || 'null');
  } catch {}
  if (cached?.data && Date.now() - cached.time < LATEST_TTL) return cached.data;
  try {
    const response = await fetch(`https://api.metalpriceapi.com/v1/latest?api_key=${encodeURIComponent(apiKey)}&base=EUR&currencies=XAU,XAG,XPT,XPD`);
    const data = await response.json();
    if (data?.success) {
      try {
        localStorage.setItem(LATEST_KEY, JSON.stringify({ time: Date.now(), data }));
      } catch {}
      return data;
    }
  } catch {}
  return cached?.data || null;
}

class InvestmentCalculator extends HTMLElement {
  #controller = null;
  #series = null; // { gold: [[date, price], ...], silver: [...] }
  #loading = false;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener('input', () => this.#update(), { signal });
    this.addEventListener('change', () => this.#update(), { signal });
    this.querySelector('form')?.addEventListener('submit', (event) => event.preventDefault(), { signal });
    this.#fetchPrices();
    window.addEventListener('gv:price-refresh', () => setTimeout(() => this.#refreshLive(), 3000), { signal });
    this.#update();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  async #fetchPrices() {
    const apiKey = this.dataset.apiKey;
    if (!apiKey) return;
    this.#loading = true;
    this.#update();
    const [rates, latest] = await Promise.all([loadPrices(apiKey), loadLatest(apiKey)]);
    this.#loading = false;
    if (rates) {
      const todayIso = isoDate(new Date());
      const dates = Object.keys(rates).filter((d) => d <= todayIso).sort();
      this.#series = {};
      for (const [metal, key] of Object.entries(KEYS)) {
        this.#series[metal] = dates
          .map((d) => [d, Number(rates[d]?.[key])])
          .filter(([, price]) => Number.isFinite(price) && price > 0);
      }
      this.#applyLive(latest);
    }
    if (this.isConnected) this.#update();
  }

  // Sets today's point of each series to the live spot price (replaces a stale same-day close or appends today).
  #applyLive(latest) {
    if (!this.#series || !latest?.rates) return;
    const todayIso = isoDate(new Date());
    for (const [metal, symbol] of Object.entries(LIVE_SYMBOLS)) {
      const series = this.#series[metal];
      const price = livePerOunce(latest.rates, symbol);
      if (!series || !Number.isFinite(price) || price <= 0) continue;
      if (series.length && series[series.length - 1][0] === todayIso) series[series.length - 1][1] = price;
      else series.push([todayIso, price]);
    }
  }

  async #refreshLive() {
    const apiKey = this.dataset.apiKey;
    if (!apiKey || !this.#series) return;
    this.#applyLive(await loadLatest(apiKey));
    if (this.isConnected) this.#update();
  }

  #fallbackRate(metal) {
    const raw = metal === 'silver' ? this.dataset.silverChange : this.dataset.goldChange;
    const value = parseFloat(String(raw ?? '').replace(',', '.'));
    return Number.isFinite(value) ? value : null;
  }

  // Returns a non-negative number, or null when the field holds something invalid.
  #read(input) {
    if (!input || input.disabled) return 0;
    const text = input.value.trim().replace(/\s/g, '').replace(',', '.');
    if (text === '') return 0;
    const value = Number(text);
    const valid = Number.isFinite(value) && value >= 0;
    input.setAttribute('aria-invalid', valid ? 'false' : 'true');
    const error = this.querySelector(`[data-calc-error="${input.dataset.calcField}"]`);
    if (error) error.hidden = valid;
    return valid ? value : null;
  }

  #format(amount) {
    return new Intl.NumberFormat('sk-SK', {
      style: 'currency',
      currency: this.dataset.currency || 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  #percent(value) {
    const sign = value > 0 ? '+' : '';
    return sign + new Intl.NumberFormat('sk-SK', { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(value) + ' %';
  }

  // Replays the investment day by day using real prices. Returns { value, invested, points }.
  #simulate(series, initial, monthly) {
    const monthSums = {};
    for (const [date, price] of series) {
      const ym = date.slice(0, 7);
      monthSums[ym] ??= [0, 0];
      monthSums[ym][0] += price;
      monthSums[ym][1] += 1;
    }

    let units = initial / series[0][1];
    let months = 0;
    let lastMonth = null;
    const points = [];
    for (const [date, price] of series) {
      const ym = date.slice(0, 7);
      if (ym !== lastMonth) {
        lastMonth = ym;
        if (monthly > 0) {
          const [sum, count] = monthSums[ym];
          units += monthly / (sum / count);
          months += 1;
        }
      }
      points.push([date, units * price]);
    }
    const value = points[points.length - 1][1];
    return { value, invested: initial + monthly * months, points };
  }

  #drawChart(points) {
    const wrap = this.querySelector('[data-calc-chart]');
    if (!wrap) return;
    const svg = wrap.querySelector('svg');
    const labels = wrap.querySelector('[data-calc-chart-years]');
    if (!points || points.length < 2) {
      wrap.hidden = true;
      return;
    }
    wrap.hidden = false;

    const width = 600;
    const height = 180;
    const pad = 4;
    const values = points.map(([, v]) => v);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || 1;
    const step = points.length > 400 ? Math.ceil(points.length / 400) : 1;
    const coords = [];
    points.forEach(([, v], i) => {
      if (i % step !== 0 && i !== points.length - 1) return;
      const x = (i / (points.length - 1)) * width;
      const y = pad + (1 - (v - min) / span) * (height - pad * 2);
      coords.push(`${x.toFixed(1)},${y.toFixed(1)}`);
    });
    const line = coords.join(' ');
    svg.querySelector('[data-line]').setAttribute('points', line);
    svg.querySelector('[data-area]').setAttribute('points', `0,${height} ${line} ${width},${height}`);

    if (labels) {
      const first = Number(points[0][0].slice(0, 4));
      const last = Number(points[points.length - 1][0].slice(0, 4));
      labels.replaceChildren(
        ...Array.from({ length: last - first + 1 }, (_, i) => {
          const span = document.createElement('span');
          span.textContent = String(first + i);
          return span;
        }),
      );
    }
  }

  #update() {
    const toggle = this.querySelector('[data-calc-monthly-toggle]');
    const monthlyWrap = this.querySelector('[data-calc-monthly-wrap]');
    const monthlyInput = this.querySelector('[data-calc-field="monthly"]');
    const useMonthly = Boolean(toggle?.checked);
    if (monthlyWrap) monthlyWrap.hidden = !useMonthly;
    if (monthlyInput) monthlyInput.disabled = !useMonthly;

    const result = this.querySelector('[data-calc-result]');
    const profit = this.querySelector('[data-calc-profit]');
    const status = this.querySelector('[data-calc-status]');
    const metal = this.querySelector('input[name="metal"]:checked')?.value || 'gold';

    const initial = this.#read(this.querySelector('[data-calc-field="initial"]'));
    const monthly = useMonthly ? this.#read(monthlyInput) : 0;

    const show = (resultText, profitText, message = '', points = null) => {
      if (result) result.textContent = resultText;
      if (profit) profit.textContent = profitText;
      if (status) {
        status.textContent = message;
        status.hidden = message === '';
      }
      this.#drawChart(points);
    };

    if (initial === null || monthly === null) return show('—', '—');

    const series = this.#series?.[metal];
    if (series && series.length > 1) {
      const { value, invested, points } = this.#simulate(series, initial, monthly);
      const gain = value - invested;
      const pct = invested > 0 ? (gain / invested) * 100 : 0;
      return show(this.#format(value), `${this.#format(gain)} (${this.#percent(pct)})`, '', points);
    }

    if (this.#loading) return show('—', '—', this.dataset.loadingText || '');

    const rate = this.#fallbackRate(metal);
    if (rate === null) return show('—', '—', this.dataset.missingText || '');

    const factor = rate / 100;
    const invested = initial + monthly * MONTHS;
    let value = initial * (1 + factor);
    for (let month = 0; month < MONTHS; month += 1) {
      value += monthly * (1 + factor * ((MONTHS - month) / MONTHS));
    }
    const gain = value - invested;
    const pct = invested > 0 ? (gain / invested) * 100 : 0;
    show(this.#format(value), `${this.#format(gain)} (${this.#percent(pct)})`);
  }
}

if (!customElements.get('s-investment-calculator')) customElements.define('s-investment-calculator', InvestmentCalculator);
